import { useNavigate } from 'react-router-dom';

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    { icon: '📊', title: 'Real-time sales dashboard', desc: 'See your sales, units, fees and net profit updated every hour from Amazon SP-API.' },
    { icon: '🧮', title: 'Profit & VAT calculator', desc: 'Instantly calculate your exact profit and VAT liability per product with UK tax rules built in.' },
    { icon: '📦', title: 'Inventory management', desc: 'Track stock levels, days of stock remaining, and get alerts before you run out.' },
    { icon: '🚨', title: 'Reorder alerts', desc: 'Never run out of stock again. Get automatic email alerts when stock drops below your threshold.' },
    { icon: '📈', title: 'BSR tracker', desc: 'Track your Best Seller Rank over time. See daily, weekly and monthly averages.' },
    { icon: '🚢', title: 'Shipment ETA calculator', desc: 'Calculate exact arrival dates for your shipments based on production and shipping times.' },
    { icon: '💰', title: 'Payout tracking', desc: 'See your upcoming Amazon payout amount and date at a glance.' },
    { icon: '📋', title: 'P&L reports', desc: 'Daily, weekly, monthly, quarterly and annual profit and loss reports in one click.' },
  ];

  const testimonials = [
    { name: 'Sarah M.', store: 'Amazon UK seller', text: 'Finally a dashboard built for UK sellers. The VAT calculator alone saves me hours every month.' },
    { name: 'James T.', store: 'FBA seller, 5 years', text: 'The reorder alerts have saved me from going out of stock twice already. Worth every penny.' },
    { name: 'Priya K.', store: 'Wholesale FBA', text: 'I can see my real profit after all fees and VAT in seconds. No more spreadsheets!' },
  ];

  return (
    <div style={{ fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif', color: '#1a1a1a' }}>

      {/* Nav */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 2rem', borderBottom: '0.5px solid rgba(0,0,0,0.08)', background: '#fff', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontSize: '18px', fontWeight: '600' }}>SellerDash</div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={() => navigate('/login')} style={{ background: 'transparent', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#666' }}>Sign in</button>
          <button onClick={() => navigate('/register')} style={{ background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '14px', cursor: 'pointer' }}>Start free trial</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '5rem 2rem 4rem', background: '#f5f4f0' }}>
        <div style={{ display: 'inline-block', background: '#EAF3DE', color: '#27500A', fontSize: '12px', fontWeight: '500', padding: '4px 12px', borderRadius: '20px', marginBottom: '1.5rem' }}>
          Built for UK Amazon sellers
        </div>
        <h1 style={{ fontSize: '48px', fontWeight: '600', lineHeight: '1.15', marginBottom: '1.25rem', maxWidth: '700px', margin: '0 auto 1.25rem' }}>
          Your Amazon business,<br />finally under control
        </h1>
        <p style={{ fontSize: '18px', color: '#666', maxWidth: '520px', margin: '0 auto 2.5rem', lineHeight: '1.6' }}>
          Real-time sales data, profit tracking, VAT calculations and inventory alerts — all in one dashboard built specifically for UK FBA sellers.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/register')} style={{ background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '10px', padding: '14px 28px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>
            Start 14-day free trial
          </button>
          <button onClick={() => navigate('/login')} style={{ background: '#fff', color: '#1a1a1a', border: '0.5px solid #ddd', borderRadius: '10px', padding: '14px 28px', fontSize: '16px', cursor: 'pointer' }}>
            Sign in
          </button>
        </div>
        <p style={{ fontSize: '13px', color: '#999', marginTop: '1rem' }}>No credit card required · £10/month after trial · Cancel anytime</p>
      </section>

      {/* Features */}
      <section style={{ padding: '5rem 2rem', maxWidth: '1100px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '32px', fontWeight: '600', textAlign: 'center', marginBottom: '3rem' }}>Everything you need to run your Amazon business</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '20px' }}>
          {features.map(f => (
            <div key={f.title} style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.5rem' }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>{f.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>{f.title}</div>
              <div style={{ fontSize: '13px', color: '#777', lineHeight: '1.6' }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '5rem 2rem', background: '#f5f4f0', textAlign: 'center' }}>
        <h2 style={{ fontSize: '32px', fontWeight: '600', marginBottom: '1rem' }}>Simple pricing</h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '3rem' }}>One plan, all features, no surprises</p>
        <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '16px', padding: '2.5rem', maxWidth: '380px', margin: '0 auto' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>SellerDash Pro</div>
          <div style={{ fontSize: '52px', fontWeight: '600', marginBottom: '4px' }}>£10</div>
          <div style={{ fontSize: '14px', color: '#888', marginBottom: '2rem' }}>per month · billed monthly</div>
          <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
            {['Real-time Amazon data sync', 'Profit & VAT calculator', 'Inventory management', 'Reorder alerts', 'BSR tracker', 'Shipment ETA calculator', 'P&L reports (daily to annual)', 'Payout tracking', '14-day free trial'].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', fontSize: '14px' }}>
                <span style={{ color: '#1D9E75', fontWeight: '600' }}>✓</span>
                {f}
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/register')} style={{ width: '100%', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '10px', padding: '14px', fontSize: '15px', cursor: 'pointer', fontWeight: '500' }}>
            Start free trial
          </button>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '12px' }}>No credit card required</p>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '5rem 2rem', maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '32px', fontWeight: '600', textAlign: 'center', marginBottom: '3rem' }}>Loved by UK Amazon sellers</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }}>
          {testimonials.map(t => (
            <div key={t.name} style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.5rem' }}>
              <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.7', marginBottom: '1rem' }}>"{t.text}"</div>
              <div style={{ fontSize: '13px', fontWeight: '600' }}>{t.name}</div>
              <div style={{ fontSize: '11px', color: '#999' }}>{t.store}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '5rem 2rem', background: '#1a1a1a', textAlign: 'center' }}>
        <h2 style={{ fontSize: '36px', fontWeight: '600', color: '#fff', marginBottom: '1rem' }}>Ready to take control of your Amazon business?</h2>
        <p style={{ fontSize: '16px', color: '#999', marginBottom: '2rem' }}>Join UK Amazon sellers using SellerDash to track their profits and grow their business.</p>
        <button onClick={() => navigate('/register')} style={{ background: '#fff', color: '#1a1a1a', border: 'none', borderRadius: '10px', padding: '14px 28px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>
          Start your free 14-day trial
        </button>
        <p style={{ fontSize: '13px', color: '#666', marginTop: '1rem' }}>No credit card required · £10/month after trial</p>
      </section>

      {/* Footer */}
      <footer style={{ padding: '2rem', background: '#111', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', color: '#555' }}>
          © 2026 SellerDash · Built for UK Amazon sellers ·
          <span style={{ cursor: 'pointer', color: '#444', margin: '0 8px' }} onClick={() => navigate('/login')}>Sign in</span> ·
          <span style={{ cursor: 'pointer', color: '#444', margin: '0 8px' }} onClick={() => navigate('/privacy')}>Privacy Policy</span> ·
          <span style={{ cursor: 'pointer', color: '#444', margin: '0 8px' }} onClick={() => navigate('/terms')}>Terms of Service</span>
        </div>
      </footer>

    </div>
  );
}